import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { IDialogueButton } from '@vestasregistry/ui-component-library';
import { PlacemarkDataService } from 'src/app/core/placemark-data.service';
import { PlacemarkService } from 'src/app/core/placemark.service';
import { ErrorHelper } from 'src/app/core/static-classes/error-helper';
import { JsonHelper } from 'src/app/core/static-classes/json-helper';
import { STANDALONE_COMPONENT_IMPORTS } from '../standalone-component-imports';

@Component({
    standalone: true,
    imports: STANDALONE_COMPONENT_IMPORTS,
    selector: 'pd-placemark',
    templateUrl: './placemark.component.html',
    styleUrl: './placemark.component.less',
})
export class PlacemarkComponent implements OnInit {
    public placemarks: PlantDesign.API.IPlacemark[];
    public listActivated: boolean;
    public editDialogueActive: boolean;
    public editLatitude: number;
    public editLongitude: number;
    public editName: string;
    public newLatitude: number;
    public newLongitude: number;
    public newName: string;
    public createMode: boolean;
    public customButtons: IDialogueButton[] = [];
    public loading: boolean;
    public error: string;
    public mode = 'create';
    public valError: { [key: string]: string } = {};
    public valid: boolean;
    public filterText: string;

    private allPlacemarks: PlantDesign.API.IPlacemark[];

    private currentId: string;

    constructor(
        private readonly placemarkService: PlacemarkService,
        private readonly placemarkDataService: PlacemarkDataService,
    ) {}

    ngOnInit(): void {
        this.placemarkDataService.placemarks$.subscribe({
            next: (response) => {
                if (response.success) {
                    this.allPlacemarks = response.result;
                    this.filterList();
                    this.validate();
                } else {
                    this.error = `Error getting placemarks: ${ErrorHelper.getErrorMessage(response.error)}`;
                }
            }
        });
        this.placemarkService.activate$.subscribe({
            next: (active) => {
                this.clear();
                this.listActivated = active;
            }
        });
        this.placemarkService.coordinateSelected$.subscribe({
            next: ([lat, long]) => {
                this.editLatitude = lat;
                this.editLongitude = long;
                this.mode = 'create';
                this.editDialogueActive = true;
            }
        });
        this.placemarkService.placemarkClick$.subscribe({
            next: (placemark) => {
                this.editPlacemark(placemark);
            }
        });
    }

    public closeList(): void {
        this.placemarkService.activate(false);
    }

    public pinClicked(): void {
        this.listActivated = false;
        this.editName = this.newName;
        this.customButtons = [
            { key: 'cancel', text: 'Cancel' }
        ];
    }

    public customButtonClicked(key: string): void {
        if (key === 'cancel' && this.customButtons.length === 1) {
            this.closeAll();
        } else if (key === 'cancel' && this.customButtons.length > 1) {
            this.closeEditDialogue();
        } else if (key === 'delete') {
            this.deletePlacemark(this.currentId);
            this.closeEditDialogue();
        }
    }

    public closeAll(): void {
        this.closeEditDialogue();
        this.placemarkService.activate(false);
    }

    private closeEditDialogue(): void {
        this.clear();
        this.editDialogueActive = false;
    }

    public filterList(): void {
        this.placemarks = JsonHelper.deepCopy(this.allPlacemarks);
        if (this.filterText) {
            this.placemarks = this.placemarks.filter(p => p.name.toLowerCase().indexOf(this.filterText.toLowerCase()) > -1);
        }
    }

    // Save placemark

    public savePlacemark(fromList = false): void {
        const placemark = this.getCurrentPlacemark(fromList);
        this.loading = true;
        this.error = null;
        const sub = this.placemarkDataService.savePlacemark(placemark).subscribe({
            next: (response) => {
                if (response.success) {
                    this.closeEditDialogue();
                    this.placemarkService.activate(false);
                    this.placemarkDataService.getPlacemarks();
                } else {
                    this.error = `Error saving placemark: ${ErrorHelper.getErrorMessage(response.error)}`;
                }
                this.loading = false;
                sub.unsubscribe();
            }
        });
    }

    public canSave(): boolean {
        return !!this.editName && !!this.editLatitude && !!this.editLongitude && this.valid;
    }

    public canSaveFromList(): boolean {
        return !!this.newName && !!this.newLatitude && !!this.newLongitude && this.valid;
    }

    // Edit placemark

    public editPlacemark(placemark: PlantDesign.API.IPlacemark): void {
        this.mode = 'edit';
        this.customButtons = [
            { key: 'cancel', text: 'Cancel' },
            { key: 'delete', text: 'Delete' },
        ];
        this.currentId = placemark.id;
        this.editName = placemark.name;
        this.editLatitude = placemark.latitude;
        this.editLongitude = placemark.longitude;
        this.editDialogueActive = true;
    }

    public updatePlacemark(): void {
        const placemark = this.getCurrentPlacemark();
        this.loading = true;
        this.error = null;
        const sub = this.placemarkDataService.updatePlacemark(placemark).subscribe({
            next: (response) => {
                if (response.success) {
                    this.closeEditDialogue();
                    this.placemarkDataService.getPlacemarks();
                } else {
                    this.error = `Error updating placemark: ${ErrorHelper.getErrorMessage(response.error)}`;
                }
                this.loading = false;
                sub.unsubscribe();
            }
        });
    }

    // Delete placemark

    public deletePlacemark(id: string): void {
        this.loading = true;
        this.error = null;
        const sub = this.placemarkDataService.deletePlacemark(id).subscribe({
            next: (response) => {
                if (response.success) {
                    this.placemarkDataService.getPlacemarks();
                } else {
                    this.error = `Error deleting placemark: ${ErrorHelper.getErrorMessage(response.error)}`;
                }
                this.loading = false;
                sub.unsubscribe();
            }
        });
    }

    // Zoom to placemark

    public zoom(placemark: PlantDesign.API.IPlacemark): void {
        this.closeAll();
        this.placemarkService.zoomToPlacemark(placemark);
    }

    // Validation

    public validate(): void {
        this.valid = true;
        this.validateLatitude('new-latitude');
        this.validateLatitude('edit-latitude');
        this.validateLongitude('new-longitude');
        this.validateLongitude('edit-longitude');
    }

    private validateLatitude(key: string): void {
        this.valError[key] = null;
        if (
            (key === 'new-latitude' && this.newLatitude && (this.newLatitude < -90 || this.newLatitude > 90)) ||
            (key === 'edit-latitude' && this.editLatitude && (this.editLatitude < -90 || this.editLatitude > 90))
        ) {
            this.valid = false;
            this.valError[key] = 'Interval from -90 to 90';
        }
    }

    private validateLongitude(key: string): void {
        this.valError[key] = null;
        if (
            (key === 'new-longitude' && this.newLongitude && (this.newLongitude < -180 || this.newLongitude > 180)) ||
            (key === 'edit-longitude' && this.editLongitude && (this.editLongitude < -180 || this.editLongitude > 180))
        ) {
            this.valid = false;
            this.valError[key] = 'Interval from -180 to 180';
        }
    }

    // Misc

    private getCurrentPlacemark(fromList = false): PlantDesign.API.IPlacemark {
        const datePipe = new DatePipe('en-US');
        const dateTime = datePipe.transform(new Date(), 'yyyy-MM-ddTHH:mm:ssZ');
        if (fromList) {
            return {
                id: this.currentId,
                updatedOn: dateTime,
                latitude: this.newLatitude,
                longitude: this.newLongitude,
                name: this.newName,
            };
        }
        return {
            id: this.currentId,
            updatedOn: dateTime,
            latitude: this.editLatitude,
            longitude: this.editLongitude,
            name: this.editName,
        };
    }

    private clear(): void {
        this.editName = null;
        this.editLatitude = null;
        this.editLongitude = null;
        this.newName = null;
        this.newLatitude = null;
        this.newLongitude = null;
    }
}
